/**
 * ReportsSection Component
 *
 * Table showing report workflows for the institution with View/Download actions.
 * Shows workflow status (draft, pending, approved) matching the /reports page behavior.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileText, Eye, Download, CheckCircle, Clock, AlertCircle, Edit3, XCircle } from 'lucide-react';
import { useRoleAssumption } from '../../../../context/RoleAssumptionContext';
import { getReportsForInstitution, type ReportWorkflow, type ReportWorkflowStatus } from '../../../../services/firestore';
import { formatFullDate } from '../../utils';
import styles from './ReportsSection.module.css';

interface ReportsSectionProps {
  /** Institution ID for fetching reports */
  institutionId: string | null;
}

// Display format for reports
interface DisplayReport {
  id: string;
  period: string;
  status: string;
  statusRaw: ReportWorkflowStatus;
  updatedAt: string;
  type: string;
}

// Map workflow status to i18n key
function getStatusKey(status: ReportWorkflowStatus): string {
  switch (status) {
    case 'draft':
      return 'status.draft';
    case 'pending_admin':
      return 'status.pendingReview';
    case 'client_review':
      return 'status.clientReview';
    case 'pending_committee':
      return 'status.committee';
    case 'approved':
      return 'status.approved';
    case 'rejected':
      return 'status.rejected';
    default:
      return 'reportsTable.unknown';
  }
}

// Get style class for status badge
function getStatusStyle(status: ReportWorkflowStatus): string {
  switch (status) {
    case 'approved':
      return styles.approved;
    case 'rejected':
      return styles.rejected;
    case 'draft':
      return styles.draft;
    default:
      return styles.pending;
  }
}

// Get icon for status
function getStatusIcon(status: ReportWorkflowStatus) {
  switch (status) {
    case 'approved':
      return <CheckCircle size={12} />;
    case 'rejected':
      return <XCircle size={12} />;
    case 'draft':
      return <Edit3 size={12} />;
    default:
      return <Clock size={12} />;
  }
}

// Map workflow to display format
function mapWorkflowToDisplay(workflow: ReportWorkflow): DisplayReport {
  return {
    id: workflow.id,
    period: workflow.period,
    status: getStatusKey(workflow.status),
    statusRaw: workflow.status,
    updatedAt: workflow.updated_at
      ? new Date(workflow.updated_at.seconds * 1000).toISOString().split('T')[0]
      : '',
    type: 'reportsTable.ratingReport',
  };
}

export default function ReportsSection({ institutionId }: ReportsSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { effectiveOrganization } = useRoleAssumption();
  const [reports, setReports] = useState<DisplayReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch report workflows when institution changes
  // Uses effectiveOrganization to respect role assumption (Platform Admin viewing as CRA Admin)
  useEffect(() => {
    const fetchReports = async () => {
      if (!institutionId || !effectiveOrganization?.id) {
        setReports([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const workflows = await getReportsForInstitution(institutionId, effectiveOrganization.id);
        // Take only the first 5 most recent
        setReports(workflows.slice(0, 5).map(mapWorkflowToDisplay));
      } catch (err) {
        console.error('Failed to fetch reports:', err);
        setError(t('reportsTable.failedToLoad'));
        setReports([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, [institutionId, effectiveOrganization?.id]);

  const handleView = (reportId: string) => {
    navigate(`/reports/${reportId}`);
  };

  const handleDownload = (reportId: string) => {
    // Navigate to report view - PDF download not yet implemented
    navigate(`/reports/${reportId}`);
  };

  if (isLoading) {
    return (
      <div className={styles.reportsSection}>
        <div className={styles.reportsHeader}>
          <div className={styles.reportsTitle}>
            <FileText size={18} />
            <span>{t('reportsTable.title')}</span>
          </div>
        </div>
        <div className={styles.loadingSkeleton}>
          {[1, 2, 3].map((i) => (
            <div key={i} className={styles.skeletonRow} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.reportsSection}>
      <div className={styles.reportsHeader}>
        <div className={styles.reportsTitle}>
          <FileText size={18} />
          <span>{t('reportsTable.title')}</span>
        </div>
      </div>

      <div className={styles.reportsTable}>
        <div className={styles.reportsTableHeader}>
          <span className={styles.colPeriod}>{t('table.period')}</span>
          <span className={styles.colType}>{t('reportsTable.type')}</span>
          <span className={styles.colStatus}>{t('table.status')}</span>
          <span className={styles.colUpdated}>{t('table.updated')}</span>
          <span className={styles.colActions}>{t('table.actions')}</span>
        </div>
        <div className={styles.reportsTableBody}>
          {reports.map((report) => (
            <div key={report.id} className={styles.reportRow}>
              <span className={styles.colPeriod}>{report.period}</span>
              <span className={styles.colType}>{t(report.type)}</span>
              <span className={styles.colStatus}>
                <span
                  className={`${styles.statusBadge} ${getStatusStyle(report.statusRaw)}`}
                >
                  {getStatusIcon(report.statusRaw)}
                  {t(report.status)}
                </span>
              </span>
              <span className={styles.colUpdated}>{formatFullDate(report.updatedAt)}</span>
              <span className={styles.colActions}>
                <button
                  className={`${styles.actionBtn} ${styles.view}`}
                  title={t('reportsTable.viewReport')}
                  onClick={() => handleView(report.id)}
                >
                  <Eye size={14} />
                </button>
                <button
                  className={`${styles.actionBtn} ${styles.download}`}
                  title={t('reportsTable.downloadPdf')}
                  onClick={() => handleDownload(report.id)}
                >
                  <Download size={14} />
                </button>
              </span>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className={styles.errorState}>
          <AlertCircle size={16} />
          <p>{error}</p>
        </div>
      )}

      {!error && reports.length === 0 && (
        <div className={styles.emptyState}>
          <FileText size={24} />
          <p>{t('reportsTable.noReportsYet')}</p>
          <span>{t('reportsTable.reportsWillAppear')}</span>
        </div>
      )}
    </div>
  );
}
